import React from "react"
import {EntityDeclineReason, EvaluationOutcome} from "../../resources/application"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {Resource} from "../../resources/common"
import {isOfficer, ProductApplication, denyProductApplication} from "../../resources/productApplication"
import {BeneficialOwner, isBeneficialOwner} from "../../resources/beneficialOwner"
import {Modal} from "../Modals/Modal"
import {refreshAndClose} from "../../utilities/modals"
import {ApplicationDenyContent, DenyConfig, getSingleEntityReasons} from "./ApplicationDeny"

function buildProductDenyConfig(
	application: ProductApplication,
	included: Resource[] | undefined,
	accessToken: string,
	deny: (
		accessToken: string,
		application: ProductApplication,
		reason: string,
		reasons: EntityDeclineReason[],
		evaluatedAt: Date
	) => void
): DenyConfig {
	const reasons = new Map<string, string[]>()
	const outcome = new Map<string, EvaluationOutcome>()
	const entityName = application.type === "individualProductApplication" ? "Individual" : "Business"

	if (application.attributes.evaluationOutcome) {
		outcome.set(entityName, application.attributes.evaluationOutcome)
	}
	getSingleEntityReasons(
		reasons,
		entityName,
		application.attributes.evaluationCodes,
		application.attributes.evaluationOutcome
	)

	if (application.type === "businessProductApplication") {
		const officer = included?.find(
			(officer) => isOfficer(officer) && officer.id === application.relationships.officer?.data.id
		)
		if (officer && isOfficer(officer)) {
			const officerAttrs = officer.attributes
			outcome.set("Officer", officerAttrs.status)
			getSingleEntityReasons(reasons, "Officer", officerAttrs.evaluationCodes, officerAttrs.status)
		}

		const beneficialOwners = included?.filter((r) => isBeneficialOwner(r)) ?? []
		beneficialOwners.forEach((boResource) => {
			if (isBeneficialOwner(boResource)) {
				const bo: BeneficialOwner = boResource.attributes
				const boName = `Beneficial owner - ${bo.fullName.first} ${bo.fullName.last}`
				outcome.set(boName, bo.status)
				getSingleEntityReasons(reasons, boName, bo.evaluationCodes, bo.status)
			}
		})
	}

	const isMultiEntity = entityName === "Business" && outcome.size > 1

	return {
		initialReasons: reasons,
		evaluationOutcome: outcome,
		isMultiEntity,
		entityName,
		denyFn: (reason, declineReasons) =>
			deny(accessToken, application, reason, declineReasons, application.attributes.updatedAt),
	}
}

export function DenyProductApplicationModal({
	application,
	close,
	onSuccess,
	accessToken,
	resources,
}: {
	application: ProductApplication
	close: () => void
	onSuccess: () => void
	accessToken: string
	resources?: Resource[]
}) {
	const [state, deny] = useAsyncResultIdle(denyProductApplication)
	const config = buildProductDenyConfig(application, resources, accessToken, deny)

	return (
		<Modal classname="deny-application-modal" close={refreshAndClose(onSuccess, close)}>
			<ApplicationDenyContent config={config} onSuccess={refreshAndClose(onSuccess, close)} state={state} />
		</Modal>
	)
}
